package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.data.dto.*
import com.arena_web.data.repository.HttpStatusException
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.HeaderNavButton
import com.arena_web.ui.components.navigation.SidebarNav
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.notifications.LocalToastState
import com.arena_web.ui.components.overlay.InteractionsModal
import com.arena_web.ui.components.overlay.PersonDetailDialog
import com.arena_web.ui.components.overlay.PostCommentsModal
import com.arena_web.ui.components.overlay.SubscribeModal
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.Language
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.localization.LocalLanguageState
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.AppSettings
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import kotlinx.browser.document
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader

@Composable
fun AppHeader(
    modifier: Modifier = Modifier,
    activeTab: SidebarTab? = null,
    onSportChanged: (String) -> Unit = {},
    // Page-specific widget mounted between the sport pill and the standard
    // action buttons (News mounts its news↔matches mode toggle here).
    extraActions: (@Composable () -> Unit)? = null
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current

    var isLoggedIn by remember { mutableStateOf(Storage.contains(StorageKeys.ACCESS_TOKEN)) }
    var userName by remember {
        mutableStateOf(
            Storage.get(StorageKeys.FULL_NAME)
                ?: Storage.get(StorageKeys.USERNAME)
                ?: ""
        )
    }
    var userHandle by remember { mutableStateOf(Storage.get(StorageKeys.USERNAME) ?: "") }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    DisposableEffect(Unit) {
        val avatarListener: (org.w3c.dom.events.Event) -> Unit = {
            avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
        }
        val nameListener: (org.w3c.dom.events.Event) -> Unit = {
            userName = Storage.get(StorageKeys.FULL_NAME)
                ?: Storage.get(StorageKeys.USERNAME)
                ?: ""
            userHandle = Storage.get(StorageKeys.USERNAME) ?: ""
        }
        kotlinx.browser.window.addEventListener("avatar-updated", avatarListener)
        kotlinx.browser.window.addEventListener("name-updated", nameListener)
        onDispose {
            kotlinx.browser.window.removeEventListener("avatar-updated", avatarListener)
            kotlinx.browser.window.removeEventListener("name-updated", nameListener)
        }
    }

    // Sports list & selection — cached in AppContainer
    val sportsList by container.sportsList.collectAsState()
    var selectedSportName by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: "All")
    }
    var selectedOtherSport by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_OTHER_SPORT)?.takeIf { it.isNotBlank() })
    }

    // Activity, notifications, wallet — cached in AppContainer
    val activityData by container.activityData.collectAsState()
    val activityCount by container.activityCount.collectAsState()
    val notificationCount by container.unreadNotificationCount.collectAsState()
    val notifications by container.notifications.collectAsState()
    val hasMoreNotifications by container.hasMoreNotifications.collectAsState()
    val notificationHandlers = rememberNotificationHandlers()

    // Single active dropdown tracker — opening one closes others
    var activeDropdown by remember { mutableStateOf<String?>(null) }

    // External signal: open profile dropdown and highlight "Switch to Business"
    val highlightBusiness by container.highlightBusinessSwitch.collectAsState()
    LaunchedEffect(highlightBusiness) {
        if (highlightBusiness) {
            activeDropdown = "profile"
        }
    }

    // Access-subscription paywall / manage popup — opened from the profile dropdown.
    var subscribeOpen by remember { mutableStateOf(false) }

    // Interactions popup state — opened from the heart button. Loads lazily
    // on first open; subsequent opens refresh in the background while showing
    // the cached lists so the popup never blanks out between sessions.
    var interactionsOpen by remember { mutableStateOf(false) }
    var interactionsLoading by remember { mutableStateOf(false) }
    var interactionsNewLikes by remember { mutableStateOf<List<PersonDto>>(emptyList()) }
    var interactionsMatches by remember { mutableStateOf<List<PersonDto>>(emptyList()) }
    var interactionsDetailFor by remember { mutableStateOf<PersonDto?>(null) }
    // Heart badge count — sourced from the container so it stays accurate
    // before the user ever opens the popup (we pre-load it via the cheap
    // `/people/interactions/count` endpoint).
    val interactionsNewLikesCount by container.interactionsNewLikesCount.collectAsState()
    LaunchedEffect(interactionsOpen) {
        if (!interactionsOpen) return@LaunchedEffect
        val repo = container.peopleRepository ?: return@LaunchedEffect
        // Skip the loading state when we already have data — the request runs
        // in the background and silently replaces it on success.
        val hadData = interactionsNewLikes.isNotEmpty() || interactionsMatches.isNotEmpty()
        if (!hadData) interactionsLoading = true
        repo.getInteractions().onSuccess { payload ->
            interactionsNewLikes = payload.newLikes
            interactionsMatches = payload.matches
            // Sync the badge count with the freshly-fetched list so any
            // backend-side delta since the initial count fetch is reflected.
            container.updateInteractionsNewLikesCount(payload.newLikes.size)
        }
        interactionsLoading = false
    }

    // All three header counters are loaded once and kept fresh by
    // `AppContainer.startHeaderSignalCollectors()` (initial cheap fetch on
    // login + WS push deltas + resync on WS reconnect). The header only
    // reads StateFlows here, so navigation no longer triggers REST.

    // Sport selector: kick off SWR on mount. `ensureSportsLoaded` reads
    // the cached list from localStorage synchronously (StateFlow already
    // populated by the time the user opens the pill) and refreshes from
    // the backend at most once per session.
    LaunchedEffect(Unit) {
        container.ensureSportsLoaded()
    }

    // Subscription state: only fetched when the user opens the profile dropdown
    // where the "Manage subscription" entry lives.
    LaunchedEffect(activeDropdown == "profile") {
        if (activeDropdown == "profile" && isLoggedIn) container.ensureSubscriptionLoaded()
    }

    // WS-driven header badge updates now live in AppContainer's
    // `startHeaderSignalCollectors()` so they survive AppHeader unmounts.

    Box(
        // .arena-header: on the light theme AppTheme locally overrides
        // --arena-bg inside this subtree, so every pill built on the
        // "InteractiveSecondary over --arena-bg" stack gets a slightly
        // darker base than the page (matches the news mode switcher).
        modifier = modifier.fillMaxWidth().sHeight(63).classNames("arena-header")
    ) {
        // Logo - left. The translateY nudges the brand mark up within the bar
        // (visual request) without touching the bar's 63-height or layout —
        // transform doesn't affect flow.
        com.arena_web.ui.components.navigation.NavAnchor("/", onNavigate = { ctx.router.navigateTo("/") }) {
            Row(
                modifier = Modifier
                    .align(Alignment.CenterStart)
                    .sGap(22)
                    .sPaddingLeft(8)
                    .cursor(Cursor.Pointer)
                    .styleModifier { property("transform", "translateY(${s(-32)})") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                com.arena_web.ui.sections.header.components.BrandLogo()
            }
        }

        // Center - SidebarNav
        Row(modifier = Modifier.align(Alignment.Center)) {
            SidebarNav(
                activeTab = activeTab,
                onTabSelected = { tab ->
                    ctx.router.navigateTo(tab.route)
                }
            )
        }

        // Right - Actions
        Row(
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .sGap(22),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Sport selector — always visible (works for guests too, the
            // sportRepository hits the public endpoint).
            SportSelectorPill(
                selectedSport = selectedSportName,
                sportsList = sportsList,
                expanded = activeDropdown == "sport",
                onToggle = { activeDropdown = if (activeDropdown == "sport") null else "sport" },
                onSportSelected = { selected ->
                    Storage.save(StorageKeys.SELECTED_SPORT_ID, selected.id)
                    Storage.save(StorageKeys.SELECTED_SPORT_NAME, selected.name)
                    // Leaving "Other" clears any custom sub-filter.
                    if (!selected.name.equals("other", ignoreCase = true)) {
                        Storage.save(StorageKeys.SELECTED_OTHER_SPORT, "")
                        selectedOtherSport = null
                    }
                    selectedSportName = selected.name
                    onSportChanged(selected.name)
                },
                selectedOtherSport = selectedOtherSport,
                onOtherSportSelected = { name ->
                    Storage.save(StorageKeys.SELECTED_OTHER_SPORT, name ?: "")
                    selectedOtherSport = name
                }
            )

            extraActions?.invoke()

            if (isLoggedIn) {
                Row(
                    modifier = Modifier.sGap(4),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    MyActivityButton(
                        activity = activityData,
                        badgeCount = activityCount,
                        sport = selectedSportName.lowercase(),
                        expanded = activeDropdown == "activity",
                        onToggle = { activeDropdown = if (activeDropdown == "activity") null else "activity" },
                        onEventClick = { eventId ->
                            activeDropdown = null
                            ctx.router.navigateTo(Routes.eventDetails(eventId))
                        }
                    )
                    FavoritesButton(
                        count = interactionsNewLikesCount,
                        onClick = { interactionsOpen = true },
                    )
                    // Light/dark theme switch beside notifications.
                    ThemeHeaderButton()
                    NotificationButton(
                        count = notificationCount,
                        notifications = notifications,
                        hasMore = hasMoreNotifications,
                        expanded = activeDropdown == "notifications",
                        onToggle = { activeDropdown = if (activeDropdown == "notifications") null else "notifications" },
                        onLoadMore = notificationHandlers.loadMore,
                        onMarkAsRead = notificationHandlers.markAsRead,
                        onMarkAllAsRead = notificationHandlers.markAllAsRead,
                        onAction = notificationHandlers.action
                    )
                    ProfilePill(
                        name = userName.ifEmpty { t.headerUserFallbackName },
                        username = userHandle,
                        avatarUrl = avatarUrl,
                        selectedSportName = selectedSportName,
                        expanded = activeDropdown == "profile",
                        highlightBusiness = highlightBusiness,
                        onToggle = {
                            activeDropdown = if (activeDropdown == "profile") null else "profile"
                            if (activeDropdown != "profile") container.clearBusinessSwitchHighlight()
                        },
                        onOpenSubscription = {
                            activeDropdown = null
                            subscribeOpen = true
                        },
                        onLoggedOut = {
                            isLoggedIn = false
                            userName = ""
                            avatarUrl = null
                        }
                    )
                }
            } else {
                com.arena_web.ui.components.auth.GoogleSignInButton(
                    onLoggedIn = { kotlinx.browser.window.location.reload() }
                )
                com.arena_web.ui.components.auth.AppleSignInButton(
                    onLoggedIn = { kotlinx.browser.window.location.reload() }
                )
            }
        }
    }

    // Interactions popup + person detail dialog. Rendered at the AppHeader
    // root so the modal sits above every other header element and survives
    // dropdown dismissals.
    InteractionsModal(
        isOpen = interactionsOpen,
        isLoading = interactionsLoading,
        newLikes = interactionsNewLikes,
        matches = interactionsMatches,
        sportName = selectedSportName,
        onDismiss = { interactionsOpen = false },
        onNewLikeClick = { person ->
            // Hand the person off to the People page via the container slot,
            // close the popup, and route to /people. The People page reads the
            // pending slot on mount, prepends the person to the swipe queue
            // (so they're the current card), and clears the slot.
            if (!person.isDeletedAccount()) {
                container.setPendingFocusPerson(person)
                interactionsOpen = false
                ctx.router.navigateTo("/people")
            }
        },
        onPersonClick = { person ->
            // Tap on a Matches card — close the popup and navigate to the
            // matched person's public profile. Skip for the anonymous shell
            // (deleted accounts have nothing to render).
            if (!person.isDeletedAccount()) {
                interactionsOpen = false
                com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, person.userId)
            }
        },
    )
    PersonDetailDialog(
        person = interactionsDetailFor,
        sportName = selectedSportName,
        onDismiss = { interactionsDetailFor = null },
    )
    SubscribeModal(
        isOpen = subscribeOpen,
        onClose = { subscribeOpen = false },
        source = "header_upgrade",
    )
}

// =============================================================================
// Header sub-components
// =============================================================================

@Composable
private fun SportSelectorPill(
    selectedSport: String,
    sportsList: List<SportDto>,
    expanded: Boolean,
    onToggle: () -> Unit,
    onSportSelected: (SportDto) -> Unit,
    selectedOtherSport: String? = null,
    onOtherSportSelected: (String?) -> Unit = {},
) {
    val t = LocalAppStrings.current
    val globalPalette = AppSettings.globalPalette
    val sportColors = SportColors.forSport(globalPalette ?: selectedSport.lowercase())

    // Close on click outside the trigger + dropdown.
    val rootId = remember { "desk-sport-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(rootId)
            if (target != null && root != null && !root.contains(target)) {
                onToggle()
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(rootId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .sHeight(48)
                .sBorderRadius(26)
                .sPadding(8, 18)
                .sGap(12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.headerSportLabel,
                modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    selectedSport.replaceFirstChar { it.uppercase() },
                    modifier = Modifier.bodyLMedium().color(sportColors.metricsPrimary)
                )
                Icon(src = AppIcons.ChevronDown)
            }
        }

        // Sport dropdown — same panel as the mobile top bar. The shared
        // `SportDropdownPanel` sorts "Other" last and handles the implicit
        // "All" logic (re-tap deselects), so the desktop caller stays a thin
        // wrapper. Anchored under the
        // trigger pill's horizontal centre (`left: 50%` + translateX(-50%))
        // so the menu sits balanced under the pill regardless of its width.
        if (expanded && sportsList.isNotEmpty()) {
            com.arena_web.ui.components.dropdown.SportDropdownPanel(
                selectedSportName = selectedSport,
                sportsList = sportsList,
                onSelect = { sport ->
                    onSportSelected(sport)
                    onToggle()
                },
                selectedOtherSport = selectedOtherSport,
                onSelectOtherSport = onOtherSportSelected,
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(56)
                    .zIndex(100)
                    .styleModifier {
                        property("left", "50%")
                        property("transform", "translateX(-50%)")
                    }
            )
        }
    }
}

@Composable
private fun MyActivityButton(
    activity: ActivityDto?,
    badgeCount: Int,
    sport: String,
    expanded: Boolean,
    onToggle: () -> Unit,
    onEventClick: (String) -> Unit = {},
) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val hasActivity = badgeCount > 0
    val rootId = "my-activity-root"

    // Lazy-load the full activity list the first time the dropdown opens.
    LaunchedEffect(expanded) {
        if (expanded) container.ensureActivityLoaded()
    }

    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { e ->
            val root = document.getElementById(rootId)
            val target = e.target as? org.w3c.dom.Node
            if (root != null && (target == null || !root.contains(target))) {
                onToggle()
            }
        }
        document.addEventListener("mousedown", handler)
        onDispose {
            document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(rootId).position(Position.Relative)) {
        HeaderNavButton(onClick = { onToggle() }) {
            Icon(src = AppIcons.Pulse)
            if (hasActivity) {
                Box(
                    modifier = Modifier
                        .sSize(12)
                        .borderRadius(50.percent)
                        .backgroundColor(sportColors.primary)
                        .position(Position.Absolute)
                        .sTop(0)
                        .sRight(2),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        badgeCount.toString(),
                        modifier = Modifier
                            .sFontSize(8)
                            .color(sportColors.onPrimary)
                            .styleModifier { property("transform", "translateY(0.5px)") }
                    )
                }
            }
        }

        if (expanded) {
            when {
                !hasActivity -> EmptyDropdown(
                    icon = AppIcons.Pulse,
                    message = t.headerNoActiveEvents,
                    onDismiss = { onToggle() }
                )
                activity != null -> ActivityDropdown(
                    activity = activity,
                    sportColors = sportColors,
                    onDismiss = { onToggle() },
                    onEventClick = onEventClick
                )
                // Loading state intentionally renders nothing (user pref).
                else -> {}
            }
        }
    }
}

@Composable
private fun ThemeHeaderButton() {
    // Round header button that toggles light/dark. Shows the glyph of the
    // theme it will switch TO: a golden sun while in dark, a crescent moon
    // while in light. Hand-drawn inline SVG (crisp, recolorable) matching
    // the profile-dropdown ThemeToggle. Backed by ThemeController, not Silk.
    val isLight = com.arena_web.ui.theme.ThemeController.isLight
    // Monochrome glyphs (currentColor) so the shared icon filter tints them
    // like every other header button: dark slate on light, pale blue on dark.
    val sunSvg =
        "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none' " +
            "stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>" +
            "<circle cx='12' cy='12' r='4.2'/>" +
            "<path d='M12 2.6v2.4M12 19v2.4M21.4 12H19M5 12H2.6" +
            "M18.7 5.3l-1.7 1.7M7 17l-1.7 1.7M18.7 18.7L17 17M7 7L5.3 5.3'/></svg>"
    val moonSvg =
        "<svg viewBox='0 0 24 24' width='100%' height='100%' fill='none'>" +
            "<path d='M20 14.5A8 8 0 0 1 9.5 4a0.6 0.6 0 0 0-0.8-0.8 8.8 8.8 0 1 0 12.1 12.1 " +
            "0.6 0.6 0 0 0-0.8-0.8z' fill='currentColor'/></svg>"
    HeaderNavButton(onClick = { com.arena_web.ui.theme.ThemeController.toggle() }) {
        // key(isLight): the SVG is injected via a ref that fires once at
        // mount — re-key so it remounts (and re-injects the right glyph)
        // whenever the theme flips.
        androidx.compose.runtime.key(isLight) {
            org.jetbrains.compose.web.dom.Div(attrs = Modifier
                .sSize(22)
                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                .styleModifier { property("line-height", "0") }
                .toAttrs {
                    ref { el ->
                        el.innerHTML = if (isLight) moonSvg else sunSvg
                        onDispose { }
                    }
                })
        }
    }
}

@Composable
private fun FavoritesButton(count: Int, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    HeaderNavButton(onClick = onClick) {
        Icon(src = AppIcons.Heart)
        if (count > 0) {
            // Same badge style the NotificationButton uses — sport-primary disc
            // pinned to the top-right of the icon with the count centred inside.
            Box(
                modifier = Modifier
                    .sSize(12)
                    .borderRadius(50.percent)
                    .backgroundColor(sportColors.primary)
                    .position(Position.Absolute)
                    .sTop(0)
                    .sRight(2),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    count.toString(),
                    modifier = Modifier
                        .sFontSize(8)
                        .color(sportColors.onPrimary)
                        .styleModifier { property("transform", "translateY(0.5px)") }
                )
            }
        }
    }
}

@Composable
private fun ActivityDropdown(activity: ActivityDto, sportColors: SportColorScheme, onDismiss: () -> Unit, onEventClick: (String) -> Unit = {}) {
    Column(
        modifier = Modifier
            .position(Position.Absolute)
            .sTop(56)
            .sWidth(348)
            .sBorderRadius(26)
            .sPaddingLeft(12).sPaddingRight(12).sPaddingTop(20).sPaddingBottom(20)
            .zIndex(100)
            .overflow(Overflow.Hidden)
            .sGap(18)
            .styleModifier {
                property("left", "50%")
                property("transform", "translateX(-50%)")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("max-height", s(520))
                property("overflow-y", "auto")
                property("box-shadow", "0 ${s(16)} ${s(48)} rgba(0, 0, 0, 0.5)")
            }
            .then(HiddenScrollbarStyle.toModifier())
    ) {
        ActivityPanelItems(
            activity = activity,
            sportColors = sportColors,
            onEventClick = onEventClick,
        )
    }
}

// Content of the activity panel (header + events + bookings) without the
// dropdown positioning shell — shared between the desktop [ActivityDropdown]
// and the mobile activity bottom sheet. Emits siblings into the caller's
// Column, so the caller owns gap/padding/scroll.
@Composable
internal fun ActivityPanelItems(
    activity: ActivityDto,
    sportColors: SportColorScheme,
    onEventClick: (String) -> Unit = {},
) {
    val t = LocalAppStrings.current
    // Header — Figma 9445:142469: 36px pulse pill, 18px "Your Active Events",
    // count in sport primary. No divider line below, just the 12px inset.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(12)
            .sPaddingBottom(12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Pulse, modifier = Modifier.sSize(16))
        }
        Row(
            modifier = Modifier.sGap(2),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                t.headerYourActiveEvents,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(18)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
            SpanText(
                "(${activity.events.size + activity.bookings.size})",
                modifier = Modifier
                    .color(sportColors.primary)
                    .sFontSize(16)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
        }
    }

    // Events list
    if (activity.events.isNotEmpty()) {
        Column(modifier = Modifier.fillMaxWidth().sGap(9)) {
            activity.events.forEachIndexed { index, event ->
                ActivityEventItem(
                    event = event,
                    index = index + 1,
                    sportColors = sportColors,
                    onClick = { onEventClick(event.id) },
                )
                if (index < activity.events.lastIndex) {
                    Box(
                        modifier = Modifier
                            .sWidth(60)
                            .height(1.px)
                            .styleModifier {
                                property(
                                    "background",
                                    "linear-gradient(90deg, " +
                                        "rgba(148, 149, 152, 0) 0%, " +
                                        "rgba(148, 149, 152, 0.27) 25%, " +
                                        "rgba(98, 99, 101, 0.3) 50%, " +
                                        "rgba(148, 149, 152, 0.27) 75%, " +
                                        "rgba(148, 149, 152, 0) 100%)"
                                )
                                property("border-radius", "1px")
                            }
                    )
                }
            }
        }
    }

    // Bookings list (kept rendering until a dedicated design lands)
    if (activity.bookings.isNotEmpty()) {
        Column(modifier = Modifier.fillMaxWidth().sGap(9)) {
            activity.bookings.forEach { booking ->
                ActivityBookingItem(booking)
            }
        }
    }
}

// Event row — Figma 9445:142480 / 9445:142511. Two layouts:
//  - regular: title | short divider | status chip inline, date + location below;
//  - pending approval (viewer's participation not confirmed yet): highlighted
//    WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40) card with a right-hand column of status chip + "Pending Approval"
//    pill against a tall divider.
@Composable
private fun ActivityEventItem(
    event: EventDto,
    index: Int,
    sportColors: SportColorScheme,
    onClick: () -> Unit = {},
) {
    var hovered by remember { mutableStateOf(false) }
    val parsed = remember(event.dateTime) { kotlin.js.Date(event.dateTime) }
    val months = remember { arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec") }
    val dateStr = remember(parsed) { "${parsed.getDate()} ${months[parsed.getMonth()]}" }
    val timeStr = remember(parsed) {
        "${parsed.getHours().toString().padStart(2, '0')} : ${parsed.getMinutes().toString().padStart(2, '0')}"
    }
    val isPendingApproval = event.myParticipantStatus?.uppercase() == "PENDING"

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .sPadding(8)
            .sGap(14)
            .cursor(Cursor.Pointer)
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .onClick { onClick() }
            .styleModifier {
                // Every event row is a WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40) card (Figma 9445:142480);
                // hover brightens it a touch on desktop.
                property("background", if (hovered) "#0B2152" else "WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40)")
                property("transition", "background 0.15s ease")
            },
        verticalAlignment = Alignment.Top,
    ) {
        // Index badge (gradient blue → transparent)
        Box(
            modifier = Modifier
                .sSize(20)
                .sBorderRadius(100)
                .styleModifier {
                    property(
                        "background",
                        "linear-gradient(0deg, #0C42FF 3.12%, rgba(102, 102, 102, 0) 60.12%)"
                    )
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                index.toString(),
                modifier = Modifier
                    .color(AppColors.PrimaryWhite)
                    .sFontSize(14)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .fontWeight(AppTypography.Regular)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
        }
        if (isPendingApproval) {
            // Info column | 50px vertical divider | status + pending pill column
            Row(
                modifier = Modifier.flexGrow(1).sGap(14).minWidth(0.px),
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.flexGrow(1).sGap(10).minWidth(0.px)) {
                    ActivityEventTitle(event.title)
                    ActivityEventInfoRows(dateStr, timeStr, event.address)
                }
                ActivityVerticalDivider(50)
                Column(
                    modifier = Modifier.sGap(8).styleModifier {
                        property("align-self", "center")
                        property("flex-shrink", "0")
                    },
                    horizontalAlignment = Alignment.Start,
                ) {
                    EventStatusChip(event.status, sportColors)
                    PendingApprovalPill()
                }
            }
        } else {
            // Title | 26px divider | status chip inline, date + location below.
            Column(modifier = Modifier.flexGrow(1).sGap(8).minWidth(0.px)) {
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(14),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    ActivityEventTitle(event.title)
                    ActivityVerticalDivider(26)
                    EventStatusChip(event.status, sportColors)
                }
                ActivityEventInfoRows(dateStr, timeStr, event.address)
            }
        }
    }
}

@Composable
private fun ActivityEventTitle(title: String) {
    SpanText(
        title,
        modifier = Modifier
            .color(AppColors.PrimaryWhite)
            .sFontSize(16)
            .sLineHeight(18)
            .sLetterSpacing(0.032)
            .fontWeight(AppTypography.Medium)
            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            .styleModifier {
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            },
    )
}

// Date + time row and location row — shared by both event item layouts.
@Composable
private fun ActivityEventInfoRows(dateStr: String, timeStr: String, address: String) {
    Column(modifier = Modifier.sGap(4).minWidth(0.px)) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.sSize(16),
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Date)
            }
            Row(
                modifier = Modifier.sGap(6),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    dateStr,
                    modifier = Modifier
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .sFontSize(14)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .fontWeight(AppTypography.Regular)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                )
                Box(
                    modifier = Modifier
                        .sSize(3)
                        .borderRadius(50.percent)
                        .backgroundColor(AppColors.Text.Secondary)
                )
                SpanText(
                    timeStr,
                    modifier = Modifier
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .sFontSize(14)
                        .sLineHeight(18)
                        .sLetterSpacing(0.028)
                        .fontWeight(AppTypography.Light)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                )
            }
        }
        Row(
            modifier = Modifier.sGap(4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.sSize(16),
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Geo)
            }
            SpanText(
                address,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(16)
                    .sLetterSpacing(0.16)
                    .fontWeight(AppTypography.Regular)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                    .styleModifier {
                        property("line-height", "1.5")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    },
            )
        }
    }
}

// Vertical soft-gradient divider, same tint as the section line.
@Composable
private fun ActivityVerticalDivider(height: Int) {
    Box(
        modifier = Modifier
            .sWidth(1)
            .sHeight(height)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(180deg, " +
                        "rgba(148, 149, 152, 0) 0%, " +
                        "rgba(148, 149, 152, 0.27) 25%, " +
                        "rgba(98, 99, 101, 0.3) 50%, " +
                        "rgba(148, 149, 152, 0.27) 75%, " +
                        "rgba(148, 149, 152, 0) 100%)"
                )
                property("border-radius", "1px")
                property("flex-shrink", "0")
                property("align-self", "center")
            }
    )
}

// "Pending Approval" outline pill — Figma 9445:142508. Standard outline-pill
// styling over the highlighted WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40) card fill.
@Composable
private fun PendingApprovalPill() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(8, 16)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, WRAP--l-hover2var(--l-hover2, #091A40), WRAP--l-hover2var(--l-hover2, #091A40)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            t.headerPendingApproval,
            modifier = Modifier
                .color(AppColors.PrimaryWhite)
                .sFontSize(12)
                .sLineHeight(12)
                .sLetterSpacing(0.012)
                .fontWeight(AppTypography.Regular)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
        )
    }
}

@Composable
private fun EventStatusChip(status: String, sportColors: SportColorScheme) {
    val t = LocalAppStrings.current
    val normalized = status.uppercase()
    val isInProgress = normalized == "IN_PROGRESS"
    val label = when (normalized) {
        "NEW", "OPEN", "ACTIVE" -> t.headerEventStatusNew
        "IN_PROGRESS" -> t.headerEventStatusInProgress
        "COMPLETED", "FINISHED" -> t.headerEventStatusCompleted
        "CANCELLED", "CANCELED" -> t.headerEventStatusCancelled
        else -> status.lowercase().replace("_", " ").replaceFirstChar { it.uppercase() }
    }
    val (bg, textColor) = if (isInProgress) {
        Color.rgb(232, 137, 8) to AppColors.PrimaryBlack
    } else {
        sportColors.primary to sportColors.onPrimary
    }
    Box(
        modifier = Modifier
            .sHeight(26)
            .sPaddingLeft(16).sPaddingRight(16)
            .sBorderRadius(22)
            .backgroundColor(bg)
            .styleModifier { property("flex-shrink", "0") },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .color(textColor)
                .sFontSize(14)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .fontWeight(AppTypography.Regular)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
        )
    }
}

@Composable
private fun ActivityBookingItem(booking: BookingDto) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(10)
            .sPadding(10, 12)
            .sGap(10)
            .styleModifier {
                property("background", "var(--arena-glass)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Calendar icon
        Icon(
            src = AppIcons.Calendar,
            modifier = Modifier.sSize(20)
        )
        // Info
        Column(modifier = Modifier.flexGrow(1).sGap(2)) {
            SpanText(
                booking.fieldName ?: t.headerBookingFallback,
                modifier = Modifier.bodySMedium().color(com.arena_web.ui.theme.ThemeVars.Text1)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Row(modifier = Modifier.sGap(8)) {
                SpanText(
                    booking.bookingDate,
                    modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                )
                SpanText(
                    "${booking.startTime} - ${booking.endTime}",
                    modifier = Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
        // Status badge
        Box(
            modifier = Modifier
                .sBorderRadius(6)
                .sPadding(2, 8)
                .styleModifier {
                    property("background", when (booking.status.uppercase()) {
                        "CONFIRMED" -> "rgba(76, 175, 80, 0.15)"
                        "PENDING" -> "rgba(255, 193, 7, 0.15)"
                        else -> "var(--l-hover, rgba(255, 255, 255, 0.06))"
                    })
                }
        ) {
            SpanText(
                booking.status.lowercase(),
                modifier = Modifier.bodySMedium().color(
                    when (booking.status.uppercase()) {
                        "CONFIRMED" -> Color("#4CAF50")
                        "PENDING" -> Color("#FFC107")
                        else -> AppColors.Text.Secondary
                    }
                )
            )
        }
    }
}

@Composable
private fun NotificationButton(
    count: Int,
    notifications: List<NotificationDto>,
    hasMore: Boolean,
    expanded: Boolean,
    onToggle: () -> Unit,
    onLoadMore: () -> Unit,
    onMarkAsRead: (String) -> Unit,
    onMarkAllAsRead: () -> Unit,
    onAction: (notificationId: String, accept: Boolean) -> Unit
) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val rootId = "notifications-root"

    // Post that should be shown in the comments modal — opened from a click on
    // a LIKE_POST or COMMENT_POST notification.
    var openPost by remember { mutableStateOf<UserPostDto?>(null) }

    // Follow state for NEW_FOLLOWER actors: actorId → isFollowing. Loaded
    // lazily once the dropdown opens, then mutated optimistically by the
    // Follow Back / Unfollow pill.
    val followRelations = remember { mutableStateMapOf<String, Boolean>() }

    // Lazy-load the first page of notifications the first time the dropdown opens.
    LaunchedEffect(expanded) {
        if (expanded) container.ensureNotificationsListLoaded()
    }

    // Auto-mark currently loaded unread notifications as read after the user
    // dwells on the open dropdown for 2s. Closing earlier cancels the coroutine
    // before the delay completes, so nothing is marked.
    LaunchedEffect(expanded) {
        if (!expanded) return@LaunchedEffect
        kotlinx.coroutines.delay(2000)
        container.notifications.value
            .filter { !it.isRead }
            .forEach { onMarkAsRead(it.id) }
    }

    // Fetch follow relations for every NEW_FOLLOWER actor visible in the
    // dropdown so the Follow Back / Unfollow label reflects reality. Each
    // actor is fetched at most once per session — the optimistic toggle below
    // keeps the cache in sync afterwards.
    LaunchedEffect(expanded, notifications) {
        if (!expanded) return@LaunchedEffect
        val repo = container.followRepository ?: return@LaunchedEffect
        val actorIds = notifications
            .filter { it.type.uppercase() == "NEW_FOLLOWER" }
            .mapNotNull { it.parseDataJson()["actorId"]?.takeIf { id -> id.isNotBlank() } }
            .distinct()
        actorIds.forEach { actorId ->
            if (actorId !in followRelations) {
                repo.getRelation(actorId).onSuccess { rel ->
                    followRelations[actorId] = rel.isFollowing
                }
            }
        }
    }

    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { e ->
            val root = document.getElementById(rootId)
            val target = e.target as? org.w3c.dom.Node
            if (root != null && (target == null || !root.contains(target))) {
                onToggle()
            }
        }
        document.addEventListener("mousedown", handler)
        onDispose {
            document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(rootId).position(Position.Relative)) {
        HeaderNavButton(onClick = { onToggle() }) {
            Icon(src = AppIcons.Notifications, modifier = Modifier.styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })

            if (count > 0) {
                Box(
                    modifier = Modifier
                        .sSize(12)
                        .borderRadius(50.percent)
                        .backgroundColor(sportColors.primary)
                        .position(Position.Absolute)
                        .sTop(0)
                        .sRight(2),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        count.toString(),
                        modifier = Modifier
                            .sFontSize(8)
                            .color(sportColors.onPrimary)
                            .styleModifier { property("transform", "translateY(0.5px)") }
                    )
                }
            }
        }

        if (expanded) {
            if (notifications.isEmpty()) {
                EmptyDropdown(
                    icon = AppIcons.Notifications,
                    message = t.headerNoNotifications,
                    onDismiss = { onToggle() }
                )
            } else {
                NotificationDropdown(
                    notifications = notifications,
                    hasMore = hasMore,
                    unreadCount = count,
                    sportColors = sportColors,
                    followRelations = followRelations,
                    onDismiss = { onToggle() },
                    onLoadMore = onLoadMore,
                    onMarkAsRead = onMarkAsRead,
                    onMarkAllAsRead = onMarkAllAsRead,
                    onAction = onAction,
                    onOpenPost = { postId, notificationId ->
                        if (postId.isBlank()) return@NotificationDropdown
                        scope.launch {
                            container.userPostRepository?.getPost(postId)?.onSuccess { post ->
                                openPost = post
                                onMarkAsRead(notificationId)
                                onToggle()
                            }
                        }
                    },
                    onAvatarClick = { actorId ->
                        if (actorId.isBlank()) return@NotificationDropdown
                        onToggle()
                        ctx.router.navigateTo(Routes.userProfile(actorId))
                    },
                    onFollowToggle = { actorId, notificationId ->
                        if (actorId.isBlank()) return@NotificationDropdown
                        val repo = container.followRepository ?: return@NotificationDropdown
                        val currentlyFollowing = followRelations[actorId] ?: false
                        // Optimistic flip — reverted on failure.
                        followRelations[actorId] = !currentlyFollowing
                        scope.launch {
                            val result = if (currentlyFollowing) repo.unfollowUser(actorId)
                                else repo.followUser(actorId)
                            result.onSuccess {
                                onMarkAsRead(notificationId)
                            }.onFailure {
                                followRelations[actorId] = currentlyFollowing
                            }
                        }
                    }
                )
            }
        }

        // Comments modal — opened for COMMENT_POST/LIKE_POST notifications.
        // Sits inside the root Box so the rest of the header layout is
        // unaffected, but outside the `expanded` branch so dismissing the
        // dropdown doesn't tear the modal down. Modal itself is
        // position:fixed, so its render location here doesn't constrain it.
        PostCommentsModal(
            isOpen = openPost != null,
            post = openPost,
            repository = container.userPostRepository,
            onDismiss = { openPost = null },
        )
    }
}

@Composable
private fun NotificationDropdown(
    notifications: List<NotificationDto>,
    hasMore: Boolean,
    unreadCount: Int,
    sportColors: SportColorScheme,
    followRelations: Map<String, Boolean>,
    onDismiss: () -> Unit,
    onLoadMore: () -> Unit,
    onMarkAsRead: (String) -> Unit,
    onMarkAllAsRead: () -> Unit,
    onAction: (notificationId: String, accept: Boolean) -> Unit,
    onOpenPost: (postId: String, notificationId: String) -> Unit,
    onAvatarClick: (actorId: String) -> Unit,
    onFollowToggle: (actorId: String, notificationId: String) -> Unit,
) {
    Column(
        modifier = Modifier
            .position(Position.Absolute)
            .sTop(56)
            .sBorderRadius(26)
            .sPaddingLeft(12).sPaddingRight(12).sPaddingTop(20).sPaddingBottom(20)
            .zIndex(100)
            .overflow(Overflow.Hidden)
            .sGap(12)
            .styleModifier {
                property("right", "0")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                // Dynamic width — fits its content between sensible min/max bounds
                property("width", "max-content")
                property("min-width", s(420))
                property("max-width", s(720))
                property("max-height", s(560))
                property("overflow-y", "auto")
                property("box-shadow", "0 ${s(16)} ${s(48)} rgba(0, 0, 0, 0.5)")
            }
            .then(HiddenScrollbarStyle.toModifier())
    ) {
        NotificationPanelItems(
            notifications = notifications,
            hasMore = hasMore,
            unreadCount = unreadCount,
            sportColors = sportColors,
            followRelations = followRelations,
            onLoadMore = onLoadMore,
            onMarkAsRead = onMarkAsRead,
            onMarkAllAsRead = onMarkAllAsRead,
            onAction = onAction,
            onOpenPost = onOpenPost,
            onAvatarClick = onAvatarClick,
            onFollowToggle = onFollowToggle,
        )
    }
}

// Content of the notifications panel (header + list + load-more) without the
// dropdown positioning shell — shared between the desktop [NotificationDropdown]
// and the mobile notifications bottom sheet. Emits siblings into the caller's
// Column, so the caller owns gap/padding/scroll.
@Composable
internal fun NotificationPanelItems(
    notifications: List<NotificationDto>,
    hasMore: Boolean,
    unreadCount: Int,
    sportColors: SportColorScheme,
    followRelations: Map<String, Boolean>,
    onLoadMore: () -> Unit,
    onMarkAsRead: (String) -> Unit,
    onMarkAllAsRead: () -> Unit,
    onAction: (notificationId: String, accept: Boolean) -> Unit,
    onOpenPost: (postId: String, notificationId: String) -> Unit,
    onAvatarClick: (actorId: String) -> Unit,
    onFollowToggle: (actorId: String, notificationId: String) -> Unit,
) {
    val t = LocalAppStrings.current
    // Header: bell pill + "Notifications (N)" + bottom border
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Notifications, modifier = Modifier.styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
        }
        Row(
            modifier = Modifier.flexGrow(1).sGap(2),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                t.headerNotificationsTitle,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(16)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
            SpanText(
                "(${notifications.size})",
                modifier = Modifier
                    .color(sportColors.primary)
                    .sFontSize(16)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
        }
        if (unreadCount > 0) {
            SpanText(
                t.headerMarkAllRead,
                modifier = Modifier
                    .sFontSize(12)
                    .color(sportColors.primary)
                    .cursor(Cursor.Pointer)
                    .onClick { onMarkAllAsRead() }
            )
        }
    }

    // Header divider — soft full-width gradient with transparent ends
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.px)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, " +
                        "rgba(148, 149, 152, 0) 0%, " +
                        "rgba(148, 149, 152, 0.27) 25%, " +
                        "rgba(98, 99, 101, 0.3) 50%, " +
                        "rgba(148, 149, 152, 0.27) 75%, " +
                        "rgba(148, 149, 152, 0) 100%)"
                )
                property("border-radius", "1px")
            }
    )

    // Notifications list — items separated by 60×1 gradient dividers
    notifications.forEachIndexed { index, notification ->
        val data = notification.parseDataJson()
        val actorId = data["actorId"].orEmpty()
        val postId = data["postId"].orEmpty()
        NotificationItem(
            notification = notification,
            sportColors = sportColors,
            onMarkAsRead = { onMarkAsRead(notification.id) },
            onAccept = if (notification.isActionable) {{ onAction(notification.id, true) }} else null,
            onDecline = if (notification.isActionable) {{ onAction(notification.id, false) }} else null,
            onAvatarClick = if (actorId.isNotBlank()) {{ onAvatarClick(actorId) }} else null,
            onOpenPost = if (postId.isNotBlank()) {{ onOpenPost(postId, notification.id) }} else null,
            isFollowingActor = followRelations[actorId] ?: false,
            onFollowToggle = if (actorId.isNotBlank()) {{ onFollowToggle(actorId, notification.id) }} else null,
        )
        if (index < notifications.lastIndex || hasMore) {
            Box(
                modifier = Modifier
                    .sWidth(60)
                    .height(1.px)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(90deg, " +
                                "rgba(148, 149, 152, 0) 0%, " +
                                "rgba(148, 149, 152, 0.27) 25%, " +
                                "rgba(98, 99, 101, 0.3) 50%, " +
                                "rgba(148, 149, 152, 0.27) 75%, " +
                                "rgba(148, 149, 152, 0) 100%)"
                        )
                        property("border-radius", "1px")
                    }
            )
        }
    }

    // Load more
    if (hasMore) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(8, 0)
                .cursor(Cursor.Pointer)
                .onClick { onLoadMore() },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                t.headerLoadMore,
                modifier = Modifier
                    .sFontSize(14)
                    .color(sportColors.primary)
            )
        }
    }
}

private fun formatNotificationTime(iso: String): String {
    val ms = kotlin.js.Date(iso).getTime()
    if (ms.isNaN()) return iso
    val diff = kotlin.js.Date().getTime() - ms
    val mins = (diff / 60_000).toInt()
    val hours = (diff / 3_600_000).toInt()
    val days = (diff / 86_400_000).toInt()
    return when {
        mins < 1 -> "Just Now"
        mins < 60 -> if (mins == 1) "1 Min Ago" else "$mins Mins Ago"
        hours < 24 -> if (hours == 1) "1 Hour Ago" else "$hours Hours Ago"
        days < 7 -> if (days == 1) "1 Day Ago" else "$days Days Ago"
        else -> {
            val d = kotlin.js.Date(ms)
            "${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}"
        }
    }
}

@Composable
private fun NotificationOutlineActionPill(
    icon: String,
    label: String,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("padding", "0 ${s(28)}")
                property("box-sizing", "border-box")
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = icon, modifier = Modifier.sSize(20))
            SpanText(
                label,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .sFontSize(16)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .fontWeight(AppTypography.Medium)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
            )
        }
    }
}

private val HEART_RED_FILTER =
    "brightness(0) saturate(100%) invert(35%) sepia(99%) saturate(7488%) hue-rotate(348deg) brightness(101%) contrast(105%)"

@Composable
private fun NotificationLikePreviewBox(
    circular: Boolean,
    imageUrls: List<String> = emptyList(),
    layout: Int = 0,
) {
    val sizeBox = if (circular) 55 else 50
    val urls = imageUrls.filter { it.isNotBlank() }.take(4)
    Box(
        modifier = Modifier
            .sSize(sizeBox)
            .styleModifier {
                property("border-radius", if (circular) "50%" else "${s(4)}")
                property("flex-shrink", "0")
                property("position", "relative")
                property("overflow", "visible")
                if (urls.isEmpty()) {
                    property(
                        "background",
                        "linear-gradient(135deg, #1e7ff5 0%, #0c42ff 100%)"
                    )
                }
            },
    ) {
        // Image grid (only when we have URLs). Inner Box clips the collage,
        // outer Box stays `overflow: visible` so the heart can overhang.
        if (urls.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .sSize(sizeBox)
                    .styleModifier {
                        property("border-radius", if (circular) "50%" else "${s(4)}")
                        property("overflow", "hidden")
                    },
            ) {
                NotificationPostCollage(urls = urls, layout = layout)
            }
        }
        // Red heart anchored at the bottom-right inset corner of the preview.
        // Heart overhangs only slightly (~4px on each side) to mirror the Figma
        // offset (`Like Event Overlay`: 58×56 frame, heart placed at right edge,
        // ~6px below the post bottom). Negative value must go INSIDE the calc()
        // expression — `-calc(...)` is invalid CSS and the browser drops the
        // whole property, causing the heart to fall back to top-left.
        Box(
            modifier = Modifier
                .sSize(16)
                .position(Position.Absolute)
                .styleModifier {
                    property("right", s(-4))
                    property("bottom", s(-4))
                },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Heart, modifier = Modifier.sSize(16).styleModifier {
                property("filter", HEART_RED_FILTER)
            })
        }
    }
}

@Composable
private fun NotificationPostCollage(urls: List<String>, layout: Int) {
    val gap = "${s(1)}"

    val rowFillMod = Modifier.fillMaxWidth().flexGrow(1).styleModifier {
        property("flex-basis", "0")
        property("min-height", "0")
    }
    val slotFillMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier {
        property("flex-basis", "0")
        property("min-width", "0")
    }

    // Mirror FeedPostCard.PostImageGrid layouts (1..6) at thumbnail scale.
    // Aspect ratios collapse to square because the host slot is 50×50, but the
    // structural arrangement of slots matches the feed renderer.
    when {
        layout == 1 || layout == 2 -> {
            NotificationImageCell(urls[0], Modifier.fillMaxSize())
        }
        layout == 3 && urls.size >= 2 -> Row(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            NotificationImageCell(urls[0], slotFillMod)
            NotificationImageCell(urls[1], slotFillMod)
        }
        layout == 4 && urls.size >= 2 -> Column(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            NotificationImageCell(urls[0], rowFillMod)
            NotificationImageCell(urls[1], rowFillMod)
        }
        layout == 5 && urls.size >= 3 -> Column(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            // Feed uses 196:150 row proportions; same here for visual parity.
            NotificationImageCell(
                urls[0],
                Modifier.fillMaxWidth().styleModifier {
                    property("flex", "196 0 0")
                    property("min-height", "0")
                }
            )
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("flex", "150 0 0")
                    property("min-height", "0")
                    property("gap", gap)
                }
            ) {
                NotificationImageCell(urls[1], slotFillMod)
                NotificationImageCell(urls[2], slotFillMod)
            }
        }
        layout == 6 && urls.size >= 3 -> Column(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("flex", "150 0 0")
                    property("min-height", "0")
                    property("gap", gap)
                }
            ) {
                NotificationImageCell(urls[0], slotFillMod)
                NotificationImageCell(urls[1], slotFillMod)
            }
            NotificationImageCell(
                urls[2],
                Modifier.fillMaxWidth().styleModifier {
                    property("flex", "196 0 0")
                    property("min-height", "0")
                }
            )
        }
        // Fallback — layout unknown (0) or doesn't match available URL count.
        // Use count-based collage so older notifications still look reasonable.
        urls.size == 1 -> NotificationImageCell(urls[0], Modifier.fillMaxSize())
        urls.size == 2 -> Row(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            NotificationImageCell(urls[0], slotFillMod)
            NotificationImageCell(urls[1], slotFillMod)
        }
        urls.size == 3 -> Row(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            NotificationImageCell(urls[0], slotFillMod)
            Column(
                modifier = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("gap", gap) }
            ) {
                NotificationImageCell(urls[1], rowFillMod)
                NotificationImageCell(urls[2], rowFillMod)
            }
        }
        else -> Column(
            modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }
        ) {
            Row(modifier = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("gap", gap) }) {
                NotificationImageCell(urls[0], slotFillMod)
                NotificationImageCell(urls[1], slotFillMod)
            }
            Row(modifier = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("gap", gap) }) {
                NotificationImageCell(urls[2], slotFillMod)
                NotificationImageCell(urls[3], slotFillMod)
            }
        }
    }
}

@Composable
private fun NotificationImageCell(url: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier.styleModifier {
            property("background", "url('$url') center/cover no-repeat")
            property("min-width", "0")
            property("min-height", "0")
        },
    )
}

internal fun extractInitials(name: String): String =
    name.trim()
        .split(Regex("[\\s:._-]+"))
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.take(1).uppercase() }

@Composable
private fun NotificationEventThumbnail(imageUrl: String?) {
    Box(
        modifier = Modifier
            .sSize(48)
            .sBorderRadius(8)
            .styleModifier {
                property("flex-shrink", "0")
                property("position", "relative")
                property("overflow", "hidden")
                if (imageUrl.isNullOrBlank()) {
                    property(
                        "background",
                        "linear-gradient(135deg, #1e7ff5 0%, #0c42ff 100%)"
                    )
                } else {
                    property("background", "url('$imageUrl') center/cover no-repeat")
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        if (imageUrl.isNullOrBlank()) {
            Icon(src = AppIcons.Calendar, modifier = Modifier.sSize(22))
        }
    }
}

private const val NOTIFICATION_CARD_CDN_BASE = "https://images.arenaproplay.com/"

@Composable
internal fun NotificationActorCard(
    initials: String,
    fallbackIcon: String? = null,
    avatarUrl: String? = null,
    cardAssetsBasePath: String? = null,
) {
    val hasAvatar = !avatarUrl.isNullOrBlank()
    val hasCardFrame = !cardAssetsBasePath.isNullOrBlank()
    val frameUrl = if (hasCardFrame) "${NOTIFICATION_CARD_CDN_BASE}$cardAssetsBasePath/card.png" else null

    Box(
        modifier = Modifier
            .styleModifier {
                property("width", s(32))
                property("height", s(48))
                property("flex-shrink", "0")
                property("border-radius", s(4))
                property("position", "relative")
                property("overflow", "hidden")
                when {
                    frameUrl != null -> {
                        // Card frame as background; the avatar will be layered
                        // on top via an absolutely-positioned image below.
                        property("background", "url('$frameUrl') center/100% 100% no-repeat")
                    }
                    hasAvatar -> {
                        property("background", "url('$avatarUrl') center/cover no-repeat, #0c42ff")
                    }
                    else -> {
                        property(
                            "background",
                            "linear-gradient(180deg, #1e7ff5 0%, #0c42ff 60%, var(--l-s3, #02040b) 100%)"
                        )
                    }
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        // Card frame + avatar (or silhouette placeholder when the actor
        // hasn't generated their card avatar yet — matches PlayerCard's
        // empty-state behaviour).
        if (frameUrl != null) {
            val effectiveAvatar = if (hasAvatar) avatarUrl!! else AppIcons.EmptyAvatar
            val isPlaceholder = !hasAvatar
            org.jetbrains.compose.web.dom.Img(
                src = effectiveAvatar,
                attrs = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("left", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("object-position", "center 55%")
                        property(
                            "transform",
                            if (isPlaceholder) "scale(0.65)" else "scale(0.87)",
                        )
                        property("transform-origin", "center 55%")
                        property("pointer-events", "none")
                    }
                    .toAttrs(),
            )
        }
        if (!hasAvatar && !hasCardFrame) {
            if (fallbackIcon != null) {
                Icon(src = fallbackIcon, modifier = Modifier.sSize(20))
            } else if (initials.isNotEmpty()) {
                SpanText(
                    initials,
                    modifier = Modifier
                        .color(AppColors.PrimaryWhite)
                        .sFontSize(10)
                        .fontWeight(AppTypography.Bold)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier {
                            property("position", "absolute")
                            property("bottom", s(5))
                            property("left", "0")
                            property("right", "0")
                            property("text-align", "center")
                            property("letter-spacing", "0.5px")
                        }
                )
            }
        }
    }
}

@Composable
private fun NotificationItem(
    notification: NotificationDto,
    sportColors: SportColorScheme,
    onMarkAsRead: () -> Unit,
    onAccept: (() -> Unit)? = null,
    onDecline: (() -> Unit)? = null,
    onAvatarClick: (() -> Unit)? = null,
    onOpenPost: (() -> Unit)? = null,
    isFollowingActor: Boolean = false,
    onFollowToggle: (() -> Unit)? = null,
) {
    val t = LocalAppStrings.current
    val fallbackIcon = when (notification.type.uppercase()) {
        "NEW_FOLLOWER" -> AppIcons.People
        "EVENT_JOIN_REQUEST", "EVENT_CONFIRMED", "EVENT_TEAM_INVITE" -> AppIcons.Calendar
        "TEAM_MEMBER_ADDED" -> AppIcons.People
        "CHAT_INVITATION", "CHAT_ADDED" -> AppIcons.Chat
        "BOOKING_REQUEST", "BOOKING_EDITED_BY_ADMIN",
        "BOOKING_CREATED_BY_ADMIN", "BOOKING_CANCELLED_BY_ADMIN" -> AppIcons.ProPlayLogo
        "REWARD_RECEIVED" -> AppIcons.Diamond
        "LIKE_POST", "LIKE_STORY" -> AppIcons.Heart
        "COMMENT_POST" -> AppIcons.Chat
        else -> AppIcons.Notifications
    }

    val type = notification.type.uppercase()
    val isReward = type == "REWARD_RECEIVED"
    val isEventConfirmed = type == "EVENT_CONFIRMED"
    val isEventJoinRequest = type == "EVENT_JOIN_REQUEST"
    val data = remember(notification.dataJson) { notification.parseDataJson() }

    // For EVENT_CONFIRMED we collapse to a single body-sourced line.
    val primaryText: String
    val secondaryText: String?
    if (isEventConfirmed) {
        primaryText = notification.body ?: notification.title
        secondaryText = null
    } else {
        primaryText = notification.title
        secondaryText = notification.body
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(10)
            .sPadding(8)
            .sBorderRadius(12)
            .cursor(Cursor.Pointer)
            .onClick { if (!notification.isRead && onAccept == null) onMarkAsRead() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Avatar slot — event thumbnail for EVENT_CONFIRMED, player-card-style otherwise.
        // Clickable when we know the actor (every type except EVENT_CONFIRMED
        // and REWARD_RECEIVED) so the viewer can jump to that user's profile.
        val avatarClickable = !isEventConfirmed && !isReward && onAvatarClick != null
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "relative")
                    property("flex-shrink", "0")
                }
                .then(
                    if (avatarClickable) Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { e ->
                            e.stopPropagation()
                            onAvatarClick?.invoke()
                        }
                    else Modifier
                )
        ) {
            when {
                isEventConfirmed -> NotificationEventThumbnail(data["eventImageUrl"])
                isEventJoinRequest -> Row(
                    modifier = Modifier.sGap(6),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    NotificationActorCard(
                        initials = extractInitials(notification.title),
                        avatarUrl = data["actorAvatarUrl"],
                        cardAssetsBasePath = data["actorCardAssetsBasePath"],
                    )
                    Icon(src = AppIcons.ArrowRight, modifier = Modifier.sSize(14).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
                    NotificationEventThumbnail(data["eventImageUrl"])
                }
                else -> NotificationActorCard(
                    initials = if (isReward) "" else extractInitials(notification.title),
                    fallbackIcon = if (isReward) fallbackIcon else null,
                    avatarUrl = if (isReward) null else data["actorAvatarUrl"],
                    cardAssetsBasePath = if (isReward) null else data["actorCardAssetsBasePath"],
                )
            }
            if (!notification.isRead) {
                Box(
                    modifier = Modifier
                        .sSize(10)
                        .borderRadius(50.percent)
                        .position(Position.Absolute)
                        .sTop(2)
                        .sRight(-2)
                        .backgroundColor(Color.hex("00E5A4"))
                        .styleModifier {
                            property("border", "${s(2)} solid var(--l-s2, #09142D)")
                        }
                )
            }
        }

        // Text block — inline primary (bold) + optional secondary (regular muted), then time
        Column(modifier = Modifier.flexGrow(1).sGap(8)) {
            Div(
                attrs = Modifier
                    .styleModifier {
                        property("color", "var(--text-secondary, #BED6F5)")
                        property("font-family",
                            "${AppTypography.PRIMARY_FONT_NAME}, ${AppTypography.PRIMARY_FONT_FALLBACK}")
                    }
                    .toAttrs()
            ) {
                org.jetbrains.compose.web.dom.Span(
                    attrs = Modifier.styleModifier {
                        property("font-size", s(16))
                        property("font-weight", AppTypography.Medium.toString())
                        property("line-height", s(18))
                        property("letter-spacing", "${s(0.032)}")
                        property("color", "var(--l-text-strong, #FFFFFF)")
                    }.toAttrs()
                ) { org.jetbrains.compose.web.dom.Text(primaryText) }
                if (!secondaryText.isNullOrBlank()) {
                    org.jetbrains.compose.web.dom.Span(
                        attrs = Modifier.styleModifier {
                            property("font-size", s(14))
                            property("font-weight", AppTypography.Regular.toString())
                            property("line-height", s(22))
                            property("letter-spacing", "${s(0.028)}")
                            property("color", "var(--arena-text-muted)")
                            property("margin-left", s(4))
                        }.toAttrs()
                    ) { org.jetbrains.compose.web.dom.Text(secondaryText) }
                }
            }
            SpanText(
                formatNotificationTime(notification.createdAt),
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                    .sFontSize(12)
                    .sLineHeight(12)
                    .sLetterSpacing(0.012)
                    .fontWeight(AppTypography.Regular)
                    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            )
        }

        // Per-type right-side widget (preview variants; rendered only when not actionable)
        if (onAccept == null && onDecline == null) {
            when (notification.type.uppercase()) {
                "REWARD_RECEIVED" -> NotificationOutlineActionPill(
                    icon = AppIcons.Eye,
                    label = t.headerActionView,
                    onClick = { onMarkAsRead() },
                )
                "LIKE_POST" -> Box(
                    modifier = Modifier
                        .then(
                            if (onOpenPost != null) Modifier
                                .cursor(Cursor.Pointer)
                                .onClick { e ->
                                    e.stopPropagation()
                                    onOpenPost.invoke()
                                }
                            else Modifier
                        )
                ) {
                    NotificationLikePreviewBox(
                        circular = false,
                        imageUrls = data["postImageUrls"]
                            ?.split("|")
                            ?.filter { it.isNotBlank() }
                            .orEmpty(),
                        layout = data["postLayout"]?.toIntOrNull() ?: 0,
                    )
                }
                "LIKE_STORY" -> NotificationLikePreviewBox(
                    circular = true,
                    imageUrls = listOfNotNull(data["storyImageUrl"]?.takeIf { it.isNotBlank() }),
                )
                "COMMENT_POST" -> NotificationOutlineActionPill(
                    icon = AppIcons.Chat,
                    label = t.headerActionReply,
                    onClick = { onOpenPost?.invoke() ?: onMarkAsRead() },
                )
                "NEW_FOLLOWER" -> NotificationOutlineActionPill(
                    icon = AppIcons.Profile,
                    label = if (isFollowingActor) t.headerActionUnfollow else t.headerActionFollowBack,
                    onClick = { onFollowToggle?.invoke() ?: onMarkAsRead() },
                )
                else -> Unit
            }
        }

        // Action area — Accept (primary) + Decline (outline) only for actionable items
        if (onAccept != null && onDecline != null) {
            // Accept — primary pill with person icon
            Box(
                modifier = Modifier
                    .sHeight(46)
                    .sBorderRadius(22)
                    .backgroundColor(sportColors.primary)
                    .cursor(Cursor.Pointer)
                    .onClick { onAccept() }
                    .styleModifier {
                        property("padding", "0 ${s(28)}")
                        property("box-sizing", "border-box")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                Row(
                    modifier = Modifier.sGap(8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Icon(src = AppIcons.Profile, modifier = Modifier.sSize(16))
                    SpanText(
                        t.headerActionAccept,
                        modifier = Modifier
                            .color(sportColors.onPrimary)
                            .sFontSize(16)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .fontWeight(AppTypography.Medium)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                    )
                }
            }
            // Decline — outline pill (canonical style)
            Box(
                modifier = Modifier
                    .sHeight(46)
                    .sBorderRadius(22)
                    .cursor(Cursor.Pointer)
                    .onClick { onDecline() }
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("padding", "0 ${s(32)}")
                        property("box-sizing", "border-box")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    t.headerActionDecline,
                    modifier = Modifier
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .sFontSize(16)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .fontWeight(AppTypography.Medium)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                )
            }
        }
    }
}

@Composable
private fun EmptyDropdown(icon: String, message: String, onDismiss: () -> Unit) {
    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .sTop(56)
            .sWidth(220)
            .sBorderRadius(16)
            .sPadding(20, 20)
            .zIndex(100)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("left", "50%")
                property("transform", "translateX(-50%)")
                property("border", "0.3px solid transparent")
                property("background",
                    "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                    "linear-gradient(0deg, var(--l-s2, #0A0D14), var(--l-s2, #0A0D14)) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.sGap(10),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(src = icon, modifier = Modifier.sSize(28))
            SpanText(
                message,
                modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") }
            )
        }
    }
}

@Composable
private fun ProfilePill(
    name: String,
    username: String,
    avatarUrl: String?,
    selectedSportName: String,
    expanded: Boolean,
    highlightBusiness: Boolean = false,
    onToggle: () -> Unit,
    onOpenSubscription: () -> Unit,
    onLoggedOut: () -> Unit,
) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    var isLoggingOut by remember { mutableStateOf(false) }
    var isDeletingAccount by remember { mutableStateOf(false) }
    var showSignOutConfirm by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val profilePillRootId = "profile-pill-root"

    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { e ->
            val root = document.getElementById(profilePillRootId)
            val target = e.target as? org.w3c.dom.Node
            if (root != null && (target == null || !root.contains(target))) {
                onToggle()
            }
        }
        document.addEventListener("mousedown", handler)
        onDispose {
            document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(profilePillRootId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .sBorderRadius(26)
                .sPaddingLeft(4)
                .sPaddingRight(16)
                .sPaddingTop(4)
                .sPaddingBottom(4)
                .sGap(24)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .sSize(44)
                    .sBorderRadius(100)
                    .overflow(Overflow.Hidden)
                    .backgroundColor(AppColors.Text.Primary),
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    key(avatarUrl) {
                        Div(attrs = {
                            ref { el ->
                                el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                    "onerror=\"this.style.display='none'\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;\" />"
                                onDispose {}
                            }
                            style { property("width", "100%"); property("height", "100%") }
                        })
                    }
                } else {
                    Icon(src = AppIcons.Profile)
                }
            }
            SpanText(
                name,
                modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Icon(src = AppIcons.ChevronDown)
        }

        // Dropdown — Figma node 9196:251486 / 9164:250544
        if (expanded) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(56).sRight(0)
                    .sBorderRadius(26)
                    .sPaddingTop(20).sPaddingBottom(20)
                    .sPaddingLeft(12).sPaddingRight(12)
                    .sGap(22)
                    .zIndex(100)
                    .styleModifier {
                        property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                        // Adaptive width: grow with the longest row (long
                        // locale strings) instead of sticking to the 320
                        // minimum — the absolutely-positioned panel otherwise
                        // shrink-wraps against its small anchor pill and wraps
                        // text. Capped so it never leaves the viewport.
                        property("width", "max-content")
                        property("min-width", "calc(320 * var(--scale, 1))")
                        property("max-width", "min(${s(480)}, calc(100vw - 32px))")
                        property("box-shadow", "0 ${s(16)} ${s(48)} rgba(0, 0, 0, 0.5)")
                    },
                horizontalAlignment = Alignment.Start,
            ) {
                ProfileMenuItems(
                    name = name,
                    username = username,
                    avatarUrl = avatarUrl,
                    selectedSportName = selectedSportName,
                    isLoggingOut = isLoggingOut,
                    onOpenSubscription = onOpenSubscription,
                    onSignOut = {
                        if (!isLoggingOut) {
                            showSignOutConfirm = true
                            onToggle()
                        }
                    },
                    onDelete = {
                        showDeleteConfirm = true
                        onToggle()
                    },
                )
            }
        }
    }

    if (showSignOutConfirm) {
        ConfirmActionDialog(
            icon = AppIcons.Exit,
            title = t.headerConfirmTitle,
            message = t.headerSignOutConfirmMessage,
            confirmLabel = t.headerSignOutConfirmLabel,
            confirmBgColor = SportColors.Football.primary,
            confirmHorizontalPadding = 48,
            isConfirming = isLoggingOut,
            onConfirm = {
                if (!isLoggingOut) {
                    isLoggingOut = true
                    scope.launch {
                        val logoutUseCase = container.getLogoutUseCase()
                        logoutUseCase()
                        container.clearAuthenticatedClients()
                        OAuthHelper.resetGoogleSession()
                        kotlinx.browser.window.location.reload()
                    }
                }
            },
            onCancel = { showSignOutConfirm = false },
        )
    }

    if (showDeleteConfirm) {
        ConfirmActionDialog(
            icon = AppIcons.Trash,
            title = t.headerConfirmTitle,
            message = t.headerDeleteConfirmMessage,
            confirmLabel = t.headerDeleteConfirmLabel,
            confirmBgColor = Color.rgb(255, 66, 66),
            isConfirming = isDeletingAccount,
            onConfirm = {
                if (!isDeletingAccount) {
                    isDeletingAccount = true
                    scope.launch {
                        val result = container.getDeleteAccountUseCase().invoke()
                        if (result.isFailure) {
                            isDeletingAccount = false
                            kotlinx.browser.window.alert(
                                t.headerDeleteProfileFailed
                            )
                            return@launch
                        }
                        container.clearAuthenticatedClients()
                        OAuthHelper.resetGoogleSession()
                        kotlinx.browser.window.location.reload()
                    }
                }
            },
            onCancel = { if (!isDeletingAccount) showDeleteConfirm = false },
        )
    }
}

// Rounded language-code pill ("EN", "UA", …) used by the language dropdown;
// highlighted = accent background (current language / trigger row).
@Composable
private fun LanguageCodeBadge(language: Language, highlighted: Boolean) {
    Box(
        modifier = Modifier
            .sBorderRadius(6)
            .sPadding(5, 7)
            .styleModifier {
                property(
                    "background",
                    if (highlighted) "${AppColors.Primary}" else "var(--l-hover, rgba(255, 255, 255, 0.08))",
                )
            },
    ) {
        SpanText(
            language.code.uppercase(),
            modifier = Modifier
                .color(if (highlighted) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                .bodySMedium(),
        )
    }
}

// Content of the profile menu (avatar header, visual style, subscription, sign out,
// delete) without the dropdown positioning shell — shared between the desktop
// [ProfilePill] dropdown and the mobile profile bottom sheet. Emits siblings
// into the caller's Column, so the caller owns gap/padding and hosts the
// confirmation dialogs behind [onSignOut] / [onDelete].
@Composable
internal fun ProfileMenuItems(
    name: String,
    username: String,
    avatarUrl: String?,
    selectedSportName: String,
    isLoggingOut: Boolean,
    onOpenSubscription: () -> Unit,
    onSignOut: () -> Unit,
    onDelete: () -> Unit,
) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    var isUploadingAvatar by remember { mutableStateOf(false) }
    var signOutHovered by remember { mutableStateOf(false) }
    var signOutPressed by remember { mutableStateOf(false) }
    var deleteHovered by remember { mutableStateOf(false) }
    var deletePressed by remember { mutableStateOf(false) }
    val globalPalette = AppSettings.globalPalette
    val isSync = globalPalette == null

    // Maps the active sport to the palette name it derives its accent color from.
    // Padel currently shares the Football palette in SportColors.
    val activePaletteName = globalPalette ?: when (selectedSportName.lowercase()) {
        "tennis" -> "tennis"
        "volleyball" -> "volleyball"
        "basketball" -> "basketball"
        else -> "football"
    }

    // ----- Profile header (avatar + name + @handle) -----
    Box(
        modifier = Modifier.fillMaxWidth().sPaddingBottom(12)
            .styleModifier {
                property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
            }
    ) {
        Row(
            modifier = Modifier
                .sGap(12)
                .cursor(if (isUploadingAvatar) Cursor.NotAllowed else Cursor.Pointer)
                .onClick {
                    if (isUploadingAvatar) return@onClick
                    pickAvatarFile { bytes, fileName, contentType ->
                        val repo = container.userRepository ?: return@pickAvatarFile
                        isUploadingAvatar = true
                        scope.launch {
                            repo.uploadMyAvatar(bytes, fileName, contentType)
                                .onSuccess { user ->
                                    user.avatarUrl?.let { Storage.save(StorageKeys.AVATAR_URL, it) }
                                    kotlinx.browser.window.dispatchEvent(
                                        org.w3c.dom.events.Event("avatar-updated"),
                                    )
                                }
                                .onFailure { console.error("Avatar upload failed: ${it.message}") }
                            isUploadingAvatar = false
                        }
                    }
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .sSize(52)
                    .sBorderRadius(100)
                    .overflow(Overflow.Hidden)
                    .backgroundColor(AppColors.Text.Primary),
                contentAlignment = Alignment.Center,
            ) {
                if (avatarUrl != null) {
                    key(avatarUrl) {
                        Div(attrs = {
                            ref { el ->
                                el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                    "onerror=\"this.style.display='none'\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;\" />"
                                onDispose {}
                            }
                            style { property("width", "100%"); property("height", "100%") }
                        })
                    }
                } else {
                    Icon(src = AppIcons.Profile)
                }
            }
            Column(
                modifier = Modifier.sGap(4),
                horizontalAlignment = Alignment.Start,
            ) {
                SpanText(
                    if (isUploadingAvatar) t.headerUploading else name,
                    modifier = Modifier
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .sFontSize(20)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .fontWeight(AppTypography.Medium)
                        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        .styleModifier { property("white-space", "nowrap") },
                )
                if (username.isNotEmpty()) {
                    SpanText(
                        "@$username",
                        modifier = Modifier
                            .color(org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)"))
                            .bodyLRegular(),
                    )
                }
            }
        }
    }

    // ----- Language -----
    // Label on the left, current language code + chevron on the right;
    // clicking opens an overlay menu on top of the content (nothing shifts) —
    // hover highlight, the active row is tinted with the accent + check.
    val languageState = LocalLanguageState.current
    var languageExpanded by remember { mutableStateOf(false) }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(16)
            .sPaddingLeft(8).sPaddingRight(8)
            .sPaddingBottom(18)
            .styleModifier {
                property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
            },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth()
                    .cursor(Cursor.Pointer)
                    .onClick { languageExpanded = !languageExpanded },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                SpanText(
                    t.headerProfileLanguage,
                    modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyXlMedium(),
                )
                Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                    LanguageCodeBadge(languageState.currentLanguage, highlighted = true)
                    Icon(
                        src = AppIcons.ChevronDown,
                        modifier = Modifier.sSize(16).styleModifier {
                            property("transform", if (languageExpanded) "rotate(180deg)" else "rotate(0deg)")
                            property("transition", "transform 200ms ease")
                        },
                    )
                }
            }
            if (languageExpanded) {
                // Click-away layer under the menu.
                Div(attrs = {
                    style {
                        property("position", "fixed")
                        property("inset", "0")
                        property("z-index", "99")
                    }
                    onClick { languageExpanded = false }
                })
                Column(
                    modifier = Modifier
                        .sBorderRadius(16)
                        .overflow(Overflow.Hidden)
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "calc(100% + 8px)")
                            property("right", "0")
                            property("width", "260px")
                            property("max-width", "100%")
                            property("z-index", "100")
                            property("border", "0.3px solid transparent")
                            property("background",
                                "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("box-shadow", "0 16px 40px rgba(0, 0, 0, 0.45)")
                        },
                ) {
                    Language.entries.forEachIndexed { index, language ->
                        val isCurrent = language == languageState.currentLanguage
                        var hovered by remember { mutableStateOf(false) }
                        Row(
                            modifier = Modifier.fillMaxWidth()
                                .sPadding(10, 12)
                                .cursor(Cursor.Pointer)
                                .onMouseEnter { hovered = true }
                                .onMouseLeave { hovered = false }
                                .styleModifier {
                                    if (index > 0) {
                                        property("border-top", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
                                    }
                                    property(
                                        "background",
                                        when {
                                            isCurrent -> "rgba(0, 97, 255, 0.12)"
                                            hovered -> "var(--l-hover, rgba(255, 255, 255, 0.06))"
                                            else -> "transparent"
                                        },
                                    )
                                    property("transition", "background 200ms ease")
                                }
                                .onClick {
                                    languageExpanded = false
                                    languageState.onChange(language)
                                },
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween,
                        ) {
                            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                                LanguageCodeBadge(language, highlighted = isCurrent)
                                SpanText(
                                    language.nativeName,
                                    modifier = Modifier
                                        .color(if (isCurrent) org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)") else com.arena_web.ui.theme.ThemeVars.Text2)
                                        .bodyLRegular(),
                                )
                                if (language == Language.ENGLISH) {
                                    SpanText(
                                        t.headerProfileLanguageRecommended,
                                        modifier = Modifier
                                            .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                                            .bodySRegular()
                                            .styleModifier { property("white-space", "nowrap") },
                                    )
                                }
                            }
                            if (isCurrent) {
                                Icon(src = AppIcons.Check, modifier = Modifier.sSize(14))
                            }
                        }
                    }
                }
            }
        }
    }

    // ----- Theme (Light/Dark) -----
    // TODO(light-theme rollout): localize the label across all 8 locales.
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingLeft(8).sPaddingRight(8)
            .sPaddingBottom(18)
            .styleModifier {
                property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
            },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            SpanText(
                t.headerLightTheme,
                modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyXlMedium(),
            )
            com.arena_web.ui.components.widgets.ThemeToggle()
        }
    }

    // ----- Visual Style (palette + sport-sync) + Subscription -----
    // The whole Visual Style block is dark-theme-only (light is locked to the
    // blue palette); the light/dark toggle lives under Language now.
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(22)
            .sPaddingLeft(8).sPaddingRight(8)
            .sPaddingBottom(18)
            .styleModifier {
                property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.08))")
            },
    ) {
        // Palette picker + sport-sync — dark-theme-only (light is locked to
        // the blue palette). The Current-plan row below shows in both themes.
        if (!com.arena_web.ui.theme.ThemeController.isLight) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(16),
                horizontalAlignment = Alignment.Start,
            ) {
                SpanText(
                    t.headerVisualStyle,
                    modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyXlMedium(),
                )
                Row(
                    modifier = Modifier.sGap(16),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    listOf("football", "tennis", "volleyball", "basketball", "cyan").forEach { paletteName ->
                        ColorOption(
                            paletteName = paletteName,
                            isSelected = activePaletteName == paletteName,
                            onClick = { AppSettings.setGlobalPalette(paletteName) },
                        )
                    }
                }
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .sBorderRadius(100)
                        .sPadding(8, 12)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("border", "0.3px solid transparent")
                            property("background",
                                "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        }
                        .onClick {
                            val newState = !isSync
                            if (newState) AppSettings.setGlobalPalette(null)
                            else AppSettings.setGlobalPalette(activePaletteName)
                        },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    SpanText(
                        t.headerSyncWithSportType,
                        modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyLRegular(),
                    )
                    SwitchToggle(
                        checked = isSync,
                        onToggle = { newState ->
                            if (newState) AppSettings.setGlobalPalette(null)
                            else AppSettings.setGlobalPalette(activePaletteName)
                        },
                    )
                }
            }
        }

        // "Current plan" row — mobile ProfileSheet parity: lightning icon,
        // "Current plan:" label, the plan name in sport-primary ("Arena Pro +"
        // for subscribers, "Free" otherwise) and, for free users, an Upgrade
        // pill on the right. The whole row opens the subscription modal.
        val isSubscribed by container.isSubscribed.collectAsState()
        val planSportColors = LocalSportColors.current
        Row(
            modifier = Modifier.fillMaxWidth()
                .sBorderRadius(100)
                .sPadding(8, 12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                }
                .onClick { onOpenSubscription() },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(src = AppIcons.Lightning, modifier = Modifier.sWidth(19).sHeight(24).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
                SpanText(
                    t.headerCurrentPlan,
                    modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyLRegular(),
                )
                SpanText(
                    if (isSubscribed) t.paywallTitle else t.headerPlanFree,
                    modifier = Modifier
                        .bodyLMedium()
                        .styleModifier {
                            property("color", planSportColors.primary.toString())
                            property("white-space", "nowrap")
                        },
                )
            }
            if (!isSubscribed) {
                Box(
                    modifier = Modifier
                        .sHeight(32)
                        .sBorderRadius(100)
                        .sPaddingLeft(16).sPaddingRight(16)
                        .backgroundColor(planSportColors.primary),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        t.headerPlanUpgrade,
                        modifier = Modifier
                            .bodyLMedium()
                            .styleModifier {
                                property("color", planSportColors.onPrimary.toString())
                                property("white-space", "nowrap")
                            },
                    )
                }
            }
        }
    }

    // ----- Sign out + Delete Profile (with hover/press highlight) -----
    Column(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalAlignment = Alignment.Start,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth()
                .sBorderRadius(100)
                .sPaddingLeft(8).sPaddingRight(8)
                .sGap(4)
                .styleModifier {
                    property("background", when {
                        signOutPressed -> "var(--l-hover-strong, rgba(255, 255, 255, 0.10))"
                        signOutHovered -> "var(--l-hover, rgba(255, 255, 255, 0.06))"
                        else -> "transparent"
                    })
                    property("transition", "background 0.15s ease")
                }
                .cursor(if (isLoggingOut) Cursor.NotAllowed else Cursor.Pointer)
                .onMouseEnter { signOutHovered = true }
                .onMouseLeave { signOutHovered = false; signOutPressed = false }
                .onMouseDown { signOutPressed = true }
                .onMouseUp { signOutPressed = false }
                .onClick { if (!isLoggingOut) onSignOut() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.sSize(44),
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Exit, modifier = Modifier.sSize(24).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
            }
            SpanText(
                if (isLoggingOut) t.headerSigningOut else t.headerSignOut,
                modifier = Modifier.color(com.arena_web.ui.theme.ThemeVars.Text2).bodyLRegular(),
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth()
                .sBorderRadius(100)
                .sPaddingLeft(8).sPaddingRight(8)
                .sGap(4)
                .styleModifier {
                    property("background", when {
                        deletePressed -> "rgba(255, 66, 66, 0.16)"
                        deleteHovered -> "rgba(255, 66, 66, 0.10)"
                        else -> "transparent"
                    })
                    property("transition", "background 0.15s ease")
                }
                .cursor(Cursor.Pointer)
                .onMouseEnter { deleteHovered = true }
                .onMouseLeave { deleteHovered = false; deletePressed = false }
                .onMouseDown { deletePressed = true }
                .onMouseUp { deletePressed = false }
                .onClick { onDelete() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.sSize(44),
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Trash, modifier = Modifier.styleModifier { property("filter", AppColors.IconDestructiveFilter) })
            }
            SpanText(
                t.headerDeleteProfile,
                modifier = Modifier
                    .color(Color.rgb(255, 66, 66))
                    .bodyLRegular(),
            )
        }
    }
}

@Composable
internal fun ConfirmActionDialog(
    icon: String,
    title: String,
    message: String,
    confirmLabel: String,
    confirmBgColor: Color,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    confirmHorizontalPadding: Int = 32,
    isConfirming: Boolean = false,
    /** Stacking order. Default sits above the app shell; callers that open it
     *  on top of a mobile BottomSheet (z-index 1100) pass a higher value. */
    zIndex: Int = 1200,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxSize()
            .onClick { onCancel() }
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", zIndex.toString())
                property("background", "rgba(0, 0, 0, 0.6)")
            },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .sWidth(502)
                .sPadding(22)
                .sGap(32)
                .sBorderRadius(22)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    // Keep the fixed 502-width panel on-screen on phones.
                    property("max-width", "calc(100vw - 32px)")
                    property("box-sizing", "border-box")
                    property("border", "0.3px solid transparent")
                    property("background",
                        "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("box-shadow", "0 ${s(16)} ${s(48)} rgba(0, 0, 0, 0.5)")
                    property("overflow", "hidden")
                },
            horizontalAlignment = Alignment.Start,
        ) {
            // Header — icon + (title + message)
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.Top,
            ) {
                Box(
                    modifier = Modifier.sSize(24).sPaddingTop(4),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(src = icon, modifier = Modifier.sSize(24))
                }
                Column(
                    modifier = Modifier.sGap(8),
                    horizontalAlignment = Alignment.Start,
                ) {
                    SpanText(
                        title,
                        modifier = Modifier
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .sFontSize(24)
                            .sLineHeight(32)
                            .sLetterSpacing(0.24)
                            .fontWeight(AppTypography.Medium)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                    )
                    SpanText(
                        message,
                        modifier = Modifier
                            .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                            .sFontSize(14)
                            .sLineHeight(22)
                            .sLetterSpacing(0.028)
                            .fontWeight(AppTypography.Regular)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                    )
                }
            }
            // Action buttons — Cancel + Confirm (right-aligned)
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .sHeight(46)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick { onCancel() }
                        .styleModifier {
                            property("border", "0.3px solid transparent")
                            property("background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("padding", "0 ${s(32)}")
                            property("box-sizing", "border-box")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        t.commonCancel,
                        modifier = Modifier
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .sFontSize(16)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .fontWeight(AppTypography.Medium)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                    )
                }
                Box(
                    modifier = Modifier
                        .sHeight(46)
                        .sPaddingLeft(confirmHorizontalPadding).sPaddingRight(confirmHorizontalPadding)
                        .sBorderRadius(100)
                        .backgroundColor(confirmBgColor)
                        .cursor(if (isConfirming) Cursor.NotAllowed else Cursor.Pointer)
                        .onClick { if (!isConfirming) onConfirm() },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        confirmLabel,
                        modifier = Modifier
                            .color(AppColors.PrimaryWhite)
                            .sFontSize(16)
                            .sLineHeight(18)
                            .sLetterSpacing(0.032)
                            .fontWeight(AppTypography.Medium)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK),
                    )
                }
            }
        }
    }
}

// Color circle for the "Visual Style" picker. The selected option grows from
// 40 → 48px to make room for a 1.5px ring and an `onPrimary`-tinted checkmark.
@Composable
private fun ColorOption(paletteName: String, isSelected: Boolean, onClick: () -> Unit) {
    val scheme = SportColors.forSport(paletteName)
    val primaryRgb = scheme.primary.toRgb()
    val onPrimaryRgb = scheme.onPrimary.toRgb()
    val swatchCss = "rgb(${primaryRgb.red}, ${primaryRgb.green}, ${primaryRgb.blue})"
    val checkCss = "rgb(${onPrimaryRgb.red}, ${onPrimaryRgb.green}, ${onPrimaryRgb.blue})"

    if (isSelected) {
        Box(
            modifier = Modifier
                .sSize(48)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "1.5px solid $swatchCss")
                    property("padding", s(4))
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 ${s(4)} ${s(4)} rgba(0, 0, 0, 0.25)")
                }
                .onClick { onClick() },
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .sSize(40)
                    .sBorderRadius(100)
                    .backgroundColor(scheme.primary),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .sSize(24)
                        .styleModifier {
                            property("mask-image", "url(${AppIcons.Check})")
                            property("-webkit-mask-image", "url(${AppIcons.Check})")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-repeat", "no-repeat")
                            property("mask-position", "center")
                            property("-webkit-mask-position", "center")
                            property("mask-size", "contain")
                            property("-webkit-mask-size", "contain")
                            property("background-color", checkCss)
                        },
                )
            }
        }
    } else {
        Box(
            modifier = Modifier
                .sSize(40)
                .sBorderRadius(100)
                .backgroundColor(scheme.primary)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("box-shadow", "0 ${s(4)} ${s(4)} rgba(0, 0, 0, 0.25)")
                }
                .onClick { onClick() },
        )
    }
}

// iOS-style toggle (51×31). Knob slides between left/right with a 200ms transition.
@Composable
private fun SwitchToggle(checked: Boolean, onToggle: (Boolean) -> Unit) {
    Box(
        modifier = Modifier
            .sWidth(51).sHeight(31)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", if (checked) "#0061FF" else "rgba(120, 120, 128, 0.5)")
                property("position", "relative")
                property("transition", "background 0.2s ease")
            }
            .onClick { e ->
                e.stopPropagation()
                onToggle(!checked)
            },
    ) {
        Box(
            modifier = Modifier
                .sSize(27)
                .sBorderRadius(100)
                .styleModifier {
                    property("background", "#FFFFFF")
                    property("position", "absolute")
                    property("top", s(2))
                    property("left", if (checked) s(22) else s(2))
                    property("transition", "left 0.2s ease")
                    property("box-shadow", "0 ${s(2)} ${s(4)} rgba(0, 0, 0, 0.3)")
                },
        )
    }
}

// Shared REST wiring for the notifications panel — load-more paging, mark-read
// and accept/decline actions over the container's cached notifications state.
// Used by both the desktop header dropdown and the mobile notifications sheet.
internal class NotificationHandlers(
    val loadMore: () -> Unit,
    val markAsRead: (String) -> Unit,
    val markAllAsRead: () -> Unit,
    val action: (notificationId: String, accept: Boolean) -> Unit,
)

@Composable
internal fun rememberNotificationHandlers(): NotificationHandlers {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    val toast = LocalToastState.current
    var page by remember { mutableStateOf(0) }

    return remember {
        val markAsRead: (String) -> Unit = { notificationId ->
            scope.launch {
                container.notificationRepository?.markAsRead(notificationId)?.onSuccess {
                    val updated = container.notifications.value.map {
                        if (it.id == notificationId) it.copy(isRead = true) else it
                    }
                    container.updateNotifications(
                        updated,
                        (container.unreadNotificationCount.value - 1).coerceAtLeast(0),
                        container.hasMoreNotifications.value,
                    )
                }
            }
        }
        NotificationHandlers(
            loadMore = {
                scope.launch {
                    val nextPage = page + 1
                    container.notificationRepository?.getNotifications(
                        mapOf("page" to nextPage.toString(), "size" to "5")
                    )?.onSuccess { pageDto ->
                        val merged = container.notifications.value + pageDto.notifications
                        page = nextPage
                        container.updateNotifications(
                            merged, pageDto.unreadCount.toInt(), pageDto.notifications.size >= 5,
                        )
                    }
                }
            },
            markAsRead = markAsRead,
            markAllAsRead = {
                scope.launch {
                    container.notificationRepository?.markAllAsRead()?.onSuccess {
                        val updated = container.notifications.value.map { it.copy(isRead = true) }
                        container.updateNotifications(updated, 0, container.hasMoreNotifications.value)
                    }
                }
            },
            action = { notificationId, accept ->
                scope.launch {
                    val notifications = container.notifications.value
                    val notification = notifications.find { it.id == notificationId } ?: return@launch
                    val data = notification.parseDataJson()
                    val result: Result<*> = when (notification.type.uppercase()) {
                        "EVENT_JOIN_REQUEST" -> {
                            val eventId = data["eventId"] ?: return@launch
                            val userId = data["userId"] ?: return@launch
                            if (accept) container.eventRepository?.confirmParticipant(eventId, userId)
                            else container.eventRepository?.rejectParticipant(eventId, userId)
                        }
                        "EVENT_TEAM_INVITE" -> {
                            val eventId = data["eventId"] ?: return@launch
                            val invitationId = data["invitationId"] ?: return@launch
                            container.eventRepository?.respondTeamInvitation(
                                eventId, invitationId, RespondTeamInvitationRequest(accept = accept)
                            )
                        }
                        else -> null
                    } ?: return@launch
                    val dropLocally = {
                        val filtered = container.notifications.value.filter { it.id != notificationId }
                        container.updateNotifications(
                            filtered,
                            (container.unreadNotificationCount.value - 1).coerceAtLeast(0),
                            container.hasMoreNotifications.value,
                        )
                    }
                    result.fold(
                        onSuccess = {
                            container.notificationRepository?.markAsRead(notificationId)
                            dropLocally()
                        },
                        onFailure = { ex ->
                            val statusCode = (ex as? HttpStatusException)?.statusCode?.value
                            val isStale = statusCode != null && statusCode in 400..499
                            if (isStale) {
                                // Backend says the request is no longer actionable
                                // (event finished/cancelled, participant gone, etc.).
                                // Drop the notification locally + mark read so it doesn't reappear.
                                container.notificationRepository?.markAsRead(notificationId)
                                dropLocally()
                                toast.warning(t.headerRequestNoLongerAvailable)
                            } else {
                                toast.error(t.headerRequestProcessFailed)
                            }
                        },
                    )
                }
            },
        )
    }
}

private fun pickAvatarFile(onPicked: (ByteArray, String, String) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = "image/jpeg,image/png,image/webp"
    input.onchange = {
        val file = input.files?.item(0)
        if (file != null) {
            val name = file.name.ifBlank { "avatar" }
            val type = file.type.ifBlank { "image/jpeg" }
            val reader = FileReader()
            reader.onload = {
                val arrayBuffer = reader.result
                if (arrayBuffer != null) {
                    @Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
                    val jsArray = js("new Uint8Array(arrayBuffer)") as org.khronos.webgl.Uint8Array
                    val bytes = ByteArray(jsArray.length) { i -> jsArray.asDynamic()[i] as Byte }
                    // Downscale/recompress before upload — a raw camera shot on
                    // a weak mobile uplink is what aborts mid-transfer.
                    com.arena_web.utils.compressForUploadThen(bytes, name, type, onPicked)
                }
            }
            reader.readAsArrayBuffer(file)
        }
    }
    input.click()
}
